// POADetailPage.tsx
// Route: /poa/:id
// Shows a saved POA and its signature status. Allows each rep to sign their row.

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Clock, Download, AlertCircle, Send, PenLine, Trash2, Mail, BellRing } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { getPOADetail, signRep, submitPOA, voidPOA, sendToClient, remindClient } from '../../services/poa';
import type { POADetail, POARepDetail } from '../../types/poa';
import { DESIGNATION_CODES, REMINDERS_ENABLED } from './poaConstants';
import SignatureModal from './SignatureModal';
import NotifyRepModal from './NotifyRepModal';

const STATUS_LABELS: Record<string, string> = {
  draft:                'Draft',
  pending_internal_sig: 'Awaiting Internal Signatures',
  pending_client_sig:   'To Be Sent for Client Sig',
  sent_to_client:       'Awaiting Client Signature',
  complete:             'Complete',
};
const STATUS_COLORS: Record<string, string> = {
  draft:                '#9aa5ce',
  pending_internal_sig: '#ff9e64',
  pending_client_sig:   '#7aa2f7',
  sent_to_client:       '#bb9af7',
  complete:             '#9ece6a',
};

function desLabel(code: string) {
  return DESIGNATION_CODES.find((d) => d.value === code)?.label ?? code;
}

function fmtDate(iso: string | null) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Whole days since an ISO timestamp, or null when there isn't one. */
function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}

/** A POA sitting with a client goes amber at a week and red at a fortnight. */
function agingColor(days: number | null): string {
  if (days === null) return 'var(--muted)';
  if (days >= 14) return '#f7768e';
  if (days >= 7)  return '#ff9e64';
  return 'var(--muted)';
}

function agingLabel(days: number | null): string {
  if (days === null) return '';
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

function fmtPeriod(m: POADetail['taxMatters'][0]) {
  if (m.periodType === 'year')  return String(m.periodStart);
  if (m.periodType === 'range') return `${m.periodStart} – ${m.periodEnd}`;
  return m.periodRaw;
}

export default function POADetailPage() {
  const { id }          = useParams<{ id: string }>();
  const navigate        = useNavigate();
  const [searchParams]  = useSearchParams();
  const { email }       = useAuth();
  const userEmail       = email?.toLowerCase() ?? '';

  const [poa,     setPoa]     = useState<POADetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);
  const [signing,    setSigning]    = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [voiding,    setVoiding]    = useState(false);
  const [sending,    setSending]    = useState(false);
  const [reminding,  setReminding]  = useState(false);
  const [modalRep,   setModalRep]   = useState<POARepDetail | null>(null);
  const [notifyRep,  setNotifyRep]  = useState<POARepDetail | null>(null);

  // ?sign=N — deep link from notification email: auto-open signature modal for rep N
  const signParam = searchParams.get('sign');

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await getPOADetail(id);
      setPoa(data);

      // Deep link: ?sign=N — open modal for that rep if it's the current user and unsigned
      if (signParam) {
        const targetOrder = Number(signParam);
        const target = data.representatives.find((r) => r.repOrder === targetOrder);
        if (target && !target.isSigned && target.peStaffUser?.toLowerCase() === userEmail) {
          setModalRep(target);
          return;
        }
      }
      // Fast path: single rep, user is that rep, not yet signed → open modal immediately
      if (
        data.representatives.length === 1 &&
        !data.representatives[0].isSigned &&
        data.representatives[0].peStaffUser?.toLowerCase() === userEmail
      ) {
        setModalRep(data.representatives[0]);
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id, userEmail, signParam]);

  useEffect(() => { load(); }, [load]);

  async function handleSignConfirm(rep: POARepDetail, sig: { type: 'drawn'; data: string }) {
    if (!poa) return;
    setModalRep(null);
    setSigning(rep.repOrder);
    try {
      await signRep({
        poaId:         poa.id,
        repOrder:      rep.repOrder,
        signatureType: sig.type,
        signatureData: sig.data,
      });
      await load();
    } catch (e: any) {
      alert(`Signature failed: ${e.message}`);
    } finally {
      setSigning(null);
    }
  }

  function handleDownloadSigned() {
    window.open(`/api/poa-pdf?id=${poa!.id}`, '_blank');
  }

  async function handleSendToClient() {
    if (!poa) return;
    if (!window.confirm(`Send Form 2848 for ${poa.taxpayerName} to ${poa.taxpayerEmail} via SafeSend?`)) return;
    setSending(true);
    try {
      await sendToClient(poa.id);
      await load();
    } catch (e: any) {
      alert(`Could not send: ${e.message}`);
    } finally {
      setSending(false);
    }
  }

  async function handleRemind() {
    if (!poa) return;
    const waited = daysSince(poa.sentToClientAt);
    const waitedLabel = waited === null ? '' : ` It was sent ${agingLabel(waited)}.`;
    if (!window.confirm(
      `Send a signature reminder to ${poa.taxpayerEmail} for ${poa.taxpayerName}?${waitedLabel}`
    )) return;
    setReminding(true);
    try {
      const result = await remindClient(poa.id);
      if (!result.hadSigningUrl) {
        alert('Reminder sent. We had no stored signing link for this POA, so the email points the client back to their original SafeSend message.');
      }
      await load();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setReminding(false);
    }
  }

  async function handleVoid() {
    if (!poa) return;
    if (!window.confirm(`Void this POA for ${poa.taxpayerName}? This cannot be undone.`)) return;
    setVoiding(true);
    try {
      await voidPOA(poa.id);
      navigate('/poa');
    } catch (e: any) {
      alert(`Could not void: ${e.message}`);
    } finally {
      setVoiding(false);
    }
  }

  async function handleSubmit() {
    if (!poa) return;
    setSubmitting(true);
    try {
      await submitPOA(poa.id);
      await load();
    } catch (e: any) {
      alert(`Submit failed: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ padding: 40, color: 'var(--muted)', fontSize: 14 }}>Loading…</div>
  );

  if (error) return (
    <div style={{ padding: 40 }}>
      <div style={{ color: 'var(--bad)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <AlertCircle size={16} /> {error}
      </div>
      <button className="btn" style={{ marginTop: 16 }} onClick={() => navigate('/poa/new')}>
        New POA
      </button>
    </div>
  );

  if (!poa) return null;

  const allSigned    = poa.representatives.every((r) => r.isSigned);
  const signedCount  = poa.representatives.filter((r) => r.isSigned).length;
  const totalReps    = poa.representatives.length;
  const statusColor  = STATUS_COLORS[poa.status] ?? 'var(--muted)';
  const statusLabel  = STATUS_LABELS[poa.status]  ?? poa.status;
  const myUnsignedRep = poa.representatives.find(
    (r) => r.peStaffUser?.toLowerCase() === userEmail && !r.isSigned
  );

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px 16px' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <button
          onClick={() => navigate('/poa')}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            fontSize: 12, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 4,
            marginBottom: 12,
          }}
        >
          ← All POAs
        </button>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
              IRS Form 2848 — Power of Attorney
            </div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{poa.taxpayerName}</h2>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>
              {poa.taxYearEarliest === poa.taxYearLatest
                ? `Tax Year ${poa.taxYearEarliest}`
                : `Tax Years ${poa.taxYearEarliest} – ${poa.taxYearLatest}`}
              {poa.peClientCode && <>&ensp;·&ensp;<span style={{ fontFamily: 'monospace', fontSize: 12 }}>{poa.peClientCode}</span></>}
              &ensp;·&ensp; Created by {poa.createdBy} &ensp;·&ensp; {fmtDate(poa.createdAt)}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {myUnsignedRep && (
              <button
                className="btn"
                style={{
                  fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 7,
                  background: 'rgba(158,206,106,.15)', borderColor: 'rgba(158,206,106,.5)',
                  color: 'var(--ok)', padding: '8px 16px',
                }}
                onClick={() => setModalRep(myUnsignedRep)}
              >
                <PenLine size={15} /> Sign Now
              </button>
            )}
            {poa.status === 'draft' && (
              <button
                className="btn"
                disabled={submitting}
                style={{
                  fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
                  background: 'rgba(122,162,255,.12)', borderColor: 'rgba(122,162,255,.4)', color: 'var(--accent)',
                }}
                onClick={handleSubmit}
              >
                <Send size={13} /> {submitting ? 'Submitting…' : 'Submit for Signatures'}
              </button>
            )}
            <button
              className="btn"
              style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}
              onClick={handleDownloadSigned}
            >
              <Download size={13} /> Download PDF
            </button>
            {REMINDERS_ENABLED && poa.status === 'sent_to_client' && poa.taxpayerEmail && (
              <button
                className="btn"
                disabled={reminding}
                onClick={handleRemind}
                title={`Remind ${poa.taxpayerEmail}`}
                style={{
                  fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
                  color: '#bb9af7', borderColor: 'rgba(187,154,247,.4)',
                  background: 'rgba(187,154,247,.1)',
                  opacity: reminding ? 0.5 : 1,
                }}
              >
                <BellRing size={13} /> {reminding ? 'Sending…' : 'Send Reminder'}
              </button>
            )}
            {poa.createdBy?.toLowerCase() === userEmail && poa.status !== 'complete' && (
              <button
                className="btn"
                disabled={voiding}
                onClick={handleVoid}
                style={{
                  fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
                  color: 'var(--bad)', borderColor: 'rgba(247,118,142,.35)',
                  background: 'rgba(247,118,142,.07)',
                  opacity: voiding ? 0.5 : 1,
                }}
              >
                <Trash2 size={13} /> {voiding ? 'Voiding…' : 'Void'}
              </button>
            )}
          </div>
        </div>

        {/* Status bar */}
        <div style={{
          marginTop: 16, padding: '10px 16px', borderRadius: 8,
          border: `1px solid ${statusColor}33`,
          background: `${statusColor}0d`,
          display: 'flex', alignItems: 'center', gap: 10, fontSize: 13,
        }}>
          <span style={{ color: statusColor, fontWeight: 600 }}>{statusLabel}</span>
          {poa.status === 'pending_internal_sig' ? (
            <span style={{ color: 'var(--muted)' }}>
              — {signedCount} of {totalReps} representative{totalReps > 1 ? 's' : ''} signed
            </span>
          ) : null}
          {poa.status === 'sent_to_client' && poa.sentToClientAt && (
            <span style={{ color: 'var(--muted)' }}>
              — sent to {poa.taxpayerEmail} on {fmtDate(poa.sentToClientAt)}
              {' '}
              <span style={{ color: agingColor(daysSince(poa.sentToClientAt)), fontWeight: 600 }}>
                ({agingLabel(daysSince(poa.sentToClientAt))})
              </span>
            </span>
          )}
        </div>

        {/* Chasing history — so the next person picking this up can see what's been done. */}
        {poa.status === 'sent_to_client' && poa.reminderCount > 0 && (
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--muted)' }}>
            {poa.reminderCount} reminder{poa.reminderCount === 1 ? '' : 's'} sent
            {poa.lastReminderAt && ` — most recently ${agingLabel(daysSince(poa.lastReminderAt))}`}.
          </div>
        )}
      </div>

      {/* Representatives + signing */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="hd" style={{ fontWeight: 600, fontSize: 14 }}>
          Part II — Declaration of Representative
          <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--muted)', marginLeft: 8 }}>
            Each representative must sign before sending to client
          </span>
        </div>
        <div className="bd" style={{ padding: 0 }}>
          {poa.representatives.map((rep) => {
            const isMe        = rep.peStaffUser?.toLowerCase() === userEmail;
            const isCreator   = poa.createdBy?.toLowerCase() === userEmail;
            const canSign     = isMe && !rep.isSigned && signing !== rep.repOrder;
            const isSigning   = signing === rep.repOrder;
            const canNotify   = isCreator && !rep.isSigned && !!rep.peStaffUser && !isMe;

            return (
              <div
                key={rep.repOrder}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 16,
                  padding: '14px 16px',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                {/* Status icon */}
                <div style={{ paddingTop: 2, flexShrink: 0 }}>
                  {rep.isSigned
                    ? <CheckCircle2 size={18} color="var(--ok)" />
                    : <Clock size={18} color="var(--muted)" />}
                </div>

                {/* Rep info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    Rep #{rep.repOrder} — {rep.repName}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                    {desLabel(rep.designationCode)}
                    {rep.licensingJurisdiction && ` · ${rep.licensingJurisdiction}`}
                    {rep.licenseNumber && ` · #${rep.licenseNumber}`}
                  </div>
                  {rep.isSigned && (
                    <div style={{ fontSize: 12, color: 'var(--ok)', marginTop: 4 }}>
                      Signed {fmtDate(rep.signedDate)} · {rep.signatureType === 'drawn' ? 'drawn signature' : `"${rep.signedByEmail}"`}
                    </div>
                  )}
                  {!rep.isSigned && !isMe && (
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                      Awaiting signature from {rep.peStaffUser ?? rep.repName}
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  {canNotify && (
                    <button
                      className="btn"
                      title={`Email ${rep.peStaffUser} a signature request`}
                      style={{
                        fontSize: 12, display: 'flex', alignItems: 'center', gap: 4,
                        color: '#bb9af7', borderColor: 'rgba(187,154,247,.35)',
                        background: 'rgba(187,154,247,.07)',
                      }}
                      onClick={() => setNotifyRep(rep)}
                    >
                      <Mail size={12} /> Notify
                    </button>
                  )}
                  {canSign && (
                    <button
                      className="btn"
                      style={{
                        fontSize: 13, fontWeight: 600,
                        background: 'rgba(122,162,255,.12)',
                        borderColor: 'rgba(122,162,255,.4)',
                        color: 'var(--accent)',
                      }}
                      onClick={() => setModalRep(rep)}
                    >
                      Sign
                    </button>
                  )}
                  {isSigning && (
                    <span style={{ fontSize: 13, color: 'var(--muted)' }}>Saving…</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Ready to send — all reps signed but not yet dispatched */}
      {allSigned && poa.status === 'pending_client_sig' && (
        <div style={{
          padding: '14px 16px', borderRadius: 8, fontSize: 13,
          background: 'rgba(122,162,255,.07)', border: '1px solid rgba(122,162,255,.22)',
          marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 3 }}>All representatives have signed.</div>
            <div style={{ color: 'var(--muted)' }}>
              {poa.taxpayerEmail
                ? `Ready to send to ${poa.taxpayerEmail} for taxpayer e-signature via SafeSend.`
                : 'Add the taxpayer\'s email address before sending for client signature.'}
            </div>
          </div>
          <button
            className="btn"
            disabled={!poa.taxpayerEmail || sending}
            onClick={handleSendToClient}
            style={{
              fontSize: 13, fontWeight: 600, flexShrink: 0,
              display: 'flex', alignItems: 'center', gap: 6,
              background: poa.taxpayerEmail ? 'rgba(122,162,255,.15)' : 'rgba(255,255,255,.05)',
              borderColor: poa.taxpayerEmail ? 'rgba(122,162,255,.4)' : 'var(--border)',
              color: poa.taxpayerEmail ? '#7aa2f7' : 'var(--muted)',
              opacity: !poa.taxpayerEmail || sending ? 0.5 : 1,
            }}
          >
            <Send size={13} /> {sending ? 'Sending…' : 'Send to Client'}
          </button>
        </div>
      )}

      {/* Sent — awaiting client signature */}
      {poa.status === 'sent_to_client' && (
        <div style={{
          padding: '14px 16px', borderRadius: 8, fontSize: 13,
          background: 'rgba(187,154,247,.07)', border: '1px solid rgba(187,154,247,.25)',
          marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <Send size={14} color="#bb9af7" style={{ flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 600, color: '#bb9af7', marginBottom: 2 }}>Sent via SafeSend</div>
            <div style={{ color: 'var(--muted)' }}>
              Awaiting taxpayer e-signature{poa.taxpayerEmail ? ` from ${poa.taxpayerEmail}` : ''}.
            </div>
          </div>
        </div>
      )}

      {/* Taxpayer + Tax Matters summary */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="card">
          <div className="hd" style={{ fontWeight: 600, fontSize: 13 }}>Taxpayer</div>
          <div className="bd" style={{ fontSize: 13 }}>
            <div style={{ fontWeight: 600 }}>{poa.taxpayerName}</div>
            <div style={{ color: 'var(--muted)', marginTop: 4, lineHeight: 1.5 }}>{poa.taxpayerAddress}</div>
            {poa.taxpayerEin   && <div style={{ marginTop: 6, color: 'var(--muted)' }}>EIN: {poa.taxpayerEin}</div>}
            {poa.taxpayerSsn   && <div style={{ marginTop: 4, color: 'var(--muted)' }}>SSN: {poa.taxpayerSsn}</div>}
            {poa.taxpayerEmail && <div style={{ marginTop: 4, color: 'var(--muted)' }}>{poa.taxpayerEmail}</div>}
          </div>
        </div>

        <div className="card">
          <div className="hd" style={{ fontWeight: 600, fontSize: 13 }}>Tax Matters</div>
          <div className="bd" style={{ fontSize: 13 }}>
            {poa.taxMatters.map((m, i) => (
              <div key={i} style={{ marginBottom: i < poa.taxMatters.length - 1 ? 10 : 0 }}>
                <div style={{ fontWeight: 600 }}>{m.matterDescription}</div>
                <div style={{ color: 'var(--muted)', fontSize: 12 }}>
                  Form {m.taxFormNumber} · {fmtPeriod(m)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Signature modal */}
      {modalRep && (
        <SignatureModal
          repName={modalRep.repName}
          onConfirm={(sig) => handleSignConfirm(modalRep, sig)}
          onClose={() => setModalRep(null)}
        />
      )}

      {/* Notify rep modal */}
      {notifyRep && (
        <NotifyRepModal
          poaId={poa.id}
          repOrder={notifyRep.repOrder}
          repName={notifyRep.repName}
          repEmail={notifyRep.peStaffUser ?? ''}
          taxpayerName={poa.taxpayerName}
          taxYear={poa.taxYearLatest}
          from={userEmail}
          onClose={() => setNotifyRep(null)}
          onSent={() => {
            setNotifyRep(null);
            alert(`Signature request sent to ${notifyRep.peStaffUser}.`);
          }}
        />
      )}
    </div>
  );
}
